import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Lock, Users, CalendarClock } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { FileImage } from "@/components/FileImage";
import { Loader } from "@/components/Loader";

export default function Events() {
  const { t } = useApp();
  const [events, setEvents] = useState(null);

  const load = () => api.get("/events").then(({ data }) => setEvents(data.events)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const rsvp = async (id) => {
    try {
      const { data } = await api.post(`/events/${id}/rsvp`);
      toast.success(data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Please try again"); }
  };

  if (events === null) return <Loader />;

  return (
    <div className="px-6 pt-8 fade-up">
      <h1 className="font-serif-cu text-2xl text-center mb-8">{t("upcoming_events")}</h1>
      {events.length === 0 ? (
        <p className="text-center text-sm text-[color:var(--cu-muted)] py-10">{t("no_events")}</p>
      ) : (
        <div className="space-y-6">
          {events.map((e) => (
            <article key={e.id} className="cu-card overflow-hidden" data-testid={`event-card-${e.id}`}>
              {e.image_path && <FileImage path={e.image_path} alt={e.title} className="w-full h-44 object-cover" />}
              <div className="p-5">
                <div className="eyebrow mb-2">{e.city} · {e.datetime_str}</div>
                <h2 className="font-serif-cu text-xl mb-3">{e.title}</h2>
                <p className="text-sm text-[color:var(--cu-muted)] leading-relaxed mb-4">{e.description}</p>

                <div className="flex items-start gap-2 text-xs text-[color:var(--cu-muted)] mb-2">
                  <MapPin size={14} className="mt-0.5 text-[color:var(--cu-gold)]" />
                  {e.my_rsvp === "confirmed" && e.private_venue ? (
                    <span className="text-[color:var(--cu-gold-light)]">{e.private_venue}</span>
                  ) : (
                    <span>{e.public_venue || t("location")}</span>
                  )}
                </div>
                {e.my_rsvp !== "confirmed" && e.private_venue && (
                  <div className="flex items-center gap-2 text-[0.7rem] text-[color:var(--cu-muted)] mb-3">
                    <Lock size={12} /> {t("private_location")}
                  </div>
                )}

                <div className="flex flex-wrap gap-4 text-xs text-[color:var(--cu-muted)] my-4">
                  {e.member_price && <span>{t("member_price")}: <span className="gold-text font-mono-cu">{e.member_price}</span></span>}
                  {e.guest_price && <span>{t("guest_price")}: <span className="gold-text font-mono-cu">{e.guest_price}</span></span>}
                  {e.capacity ? <span className="flex items-center gap-1"><Users size={12} /> {e.rsvp_count}/{e.capacity}</span> : null}
                  {e.rsvp_deadline && <span className="flex items-center gap-1"><CalendarClock size={12} /> {t("rsvp_by")} {e.rsvp_deadline}</span>}
                </div>

                {e.my_rsvp ? (
                  <div data-testid={`event-rsvp-status-${e.id}`} className="text-center py-3 rounded-full text-xs uppercase tracking-[0.15em] font-mono-cu" style={{ color: e.my_rsvp === "confirmed" ? "#10B981" : "#EAB308", border: `1px solid ${e.my_rsvp === "confirmed" ? "#10B98155" : "#EAB30855"}` }}>
                    {e.my_rsvp === "confirmed" ? t("rsvp_confirmed") : t("rsvp_requested")}
                  </div>
                ) : (
                  <button onClick={() => rsvp(e.id)} data-testid={`event-rsvp-button-${e.id}`} className="btn-gold w-full py-3 text-xs">{t("rsvp")}</button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
